/**
 * Daily nutrient targets from the Dietary Reference Intakes (NASEM).
 *
 * Adults 19+ only. Values checked against the NASEM DRI summary tables
 * (NCBI Bookshelf NBK545442, Appendix J: vitamins, elements, and the two
 * UL tables) on 2026-09-19.
 *
 *   RDA  Recommended Dietary Allowance: covers ~97.5% of healthy people.
 *   AI   Adequate Intake: used when there isn't enough data for an RDA.
 *   UL   Tolerable Upper Intake Level: above this, risk of harm rises.
 *   CDRR Chronic Disease Risk Reduction intake (sodium): cut back if above.
 *   DGA  Dietary Guidelines for Americans limit (% of calories).
 *
 * Some ULs apply only to supplements / fortified or synthetic forms
 * (magnesium, niacin, folate, vitamin E) or only to preformed vitamin A.
 * Food logs can't tell those apart, so for them the UL is shown as
 * information and never flagged as "over".
 *
 * Keys are the nutrient catalog keys; units match the catalog
 * (vitamin D in IU, fluoride in µg, copper in mg).
 */

export type Sex = 'male' | 'female';
export type TargetKind = 'RDA' | 'AI' | 'CDRR' | 'DGA';

export interface NutrientTarget {
  key: string;
  /** The amount to reach (RDA/AI), or the amount to stay under (CDRR/DGA). */
  amount: number;
  kind: TargetKind;
  /** true = a ceiling to stay under (sodium CDRR, saturated fat, added sugars). */
  isLimit: boolean;
  ul: number | null;
  /** true = the UL only applies to supplements/fortified/synthetic forms, so food totals aren't flagged. */
  ulSupplementOnly: boolean;
  note?: string;
}

export interface DriInput {
  sex: Sex | null;
  ageYears: number | null;
  /** For per-kg targets (protein, essential amino acids). */
  weightKg?: number | null;
  /** The day's calorie target, for the %-of-calories limits (saturated fat, added sugars). */
  energyKcal?: number | null;
}

export interface DriResult {
  targets: Map<string, NutrientTarget>;
  /** Why targets are missing or limited. */
  notes: string[];
}

export const DRI_CITATIONS = [
  { key: 'nasem2019', text: 'National Academies of Sciences, Engineering, and Medicine. Dietary Reference Intakes for Sodium and Potassium. Washington, DC: The National Academies Press; 2019. Appendix J: DRI summary tables.', url: 'https://www.ncbi.nlm.nih.gov/books/NBK545442/' },
  { key: 'iom2005', text: 'Institute of Medicine. Dietary Reference Intakes for Energy, Carbohydrate, Fiber, Fat, Fatty Acids, Cholesterol, Protein, and Amino Acids. Washington, DC: The National Academies Press; 2005.', url: 'https://doi.org/10.17226/10490' },
  { key: 'dga2020', text: 'U.S. Department of Agriculture and U.S. Department of Health and Human Services. Dietary Guidelines for Americans, 2020–2025. 9th ed. 2020.', url: 'https://www.dietaryguidelines.gov/' },
] as const;

/** Essential amino acid RDAs for adults, mg per kg body weight per day (IOM 2005). Pairs (Met+Cys, Phe+Tyr) are skipped because the catalog lists them separately. */
export const EAA_MG_PER_KG: Readonly<Record<string, number>> = {
  histidine_g: 14, isoleucine_g: 19, leucine_g: 42, lysine_g: 38, threonine_g: 20, tryptophan_g: 5, valine_g: 24,
};

const IU_PER_UG_VIT_D = 40;

export function driTargets(input: DriInput): DriResult {
  const notes: string[] = [];
  const targets = new Map<string, NutrientTarget>();
  const { sex, ageYears } = input;
  if (sex === null || ageYears === null) {
    notes.push('Add your sex and birth date in Profile to see your daily targets.');
    return { targets, notes };
  }
  if (ageYears < 19) {
    notes.push('Targets here are for adults (19+). Teen needs differ, so none are shown.');
    return { targets, notes };
  }
  const m = sex === 'male';
  const a = ageYears;
  const over50 = a >= 51;
  const over70 = a > 70;

  const set = (key: string, amount: number, kind: TargetKind, ul: number | null = null, ulSupplementOnly = false, note?: string): void => {
    targets.set(key, { key, amount, kind, isLimit: kind === 'CDRR' || kind === 'DGA', ul, ulSupplementOnly, ...(note ? { note } : {}) });
  };

  // ---- Vitamins ---------------------------------------------------------
  set('vit_a_ug', m ? 900 : 700, 'RDA', 3000, true, 'UL applies to preformed vitamin A (retinol) only.');
  set('vit_c_mg', m ? 90 : 75, 'RDA', 2000);
  set('vit_d_iu', (over70 ? 20 : 15) * IU_PER_UG_VIT_D, 'RDA', 100 * IU_PER_UG_VIT_D);
  set('vit_e_mg', 15, 'RDA', 1000, true, 'UL applies to supplements and fortified foods only.');
  set('vit_k_ug', m ? 120 : 90, 'AI');
  set('vit_b1_mg', m ? 1.2 : 1.1, 'RDA');
  set('vit_b2_mg', m ? 1.3 : 1.1, 'RDA');
  set('vit_b3_mg', m ? 16 : 14, 'RDA', 35, true, 'UL applies to supplements and fortified foods only.');
  set('vit_b5_mg', 5, 'AI');
  set('vit_b6_mg', over50 ? (m ? 1.7 : 1.5) : 1.3, 'RDA', 100);
  set('biotin_ug', 30, 'AI');
  set('folate_ug', 400, 'RDA', 1000, true, 'UL applies to folic acid from supplements and fortified foods only.');
  set('vit_b12_ug', 2.4, 'RDA');
  set('choline_mg', m ? 550 : 425, 'AI', 3500);

  // ---- Minerals ---------------------------------------------------------
  set('calcium_mg', (m ? over70 : over50) ? 1200 : 1000, 'RDA', over50 ? 2000 : 2500);
  set('chromium_ug', m ? (over50 ? 30 : 35) : (over50 ? 20 : 25), 'AI');
  set('copper_mg', 0.9, 'RDA', 10);
  set('fluoride_ug', m ? 4000 : 3000, 'AI', 10000);
  set('iodine_ug', 150, 'RDA', 1100);
  set('iron_mg', m || over50 ? 8 : 18, 'RDA', 45);
  set('magnesium_mg', m ? (a <= 30 ? 400 : 420) : (a <= 30 ? 310 : 320), 'RDA', 350, true, 'UL applies to supplements and medicines only, not food.');
  set('manganese_mg', m ? 2.3 : 1.8, 'AI', 11);
  set('molybdenum_ug', 45, 'RDA', 2000);
  set('phosphorus_mg', 700, 'RDA', over70 ? 3000 : 4000);
  set('potassium_mg', m ? 3400 : 2600, 'AI');
  set('selenium_ug', 55, 'RDA', 400);
  set('zinc_mg', m ? 11 : 8, 'RDA', 40);
  set('sodium_mg', 2300, 'CDRR', null, false, 'Adequate intake is 1,500 mg. Above 2,300 mg, cutting back lowers chronic disease risk.');

  // ---- Fats, fiber, protein ---------------------------------------------
  set('la_g', m ? (over50 ? 14 : 17) : (over50 ? 11 : 12), 'AI');
  set('ala_g', m ? 1.6 : 1.1, 'AI');
  // Fiber AI is 14 g per 1,000 kcal; the fixed table values assume typical intakes, so use the calorie target when there is one.
  const kcalForFiber = input.energyKcal ?? null;
  if (kcalForFiber !== null && kcalForFiber > 0) set('fiber_g', (14 * kcalForFiber) / 1000, 'AI', null, false, '14 g per 1,000 kcal of your calorie target.');
  else set('fiber_g', m ? (over50 ? 30 : 38) : (over50 ? 21 : 25), 'AI', null, false, 'Table value; set a calorie target to scale it (14 g per 1,000 kcal).');

  const w = input.weightKg ?? null;
  if (w !== null && w > 0) {
    set('protein_g', 0.8 * w, 'RDA', null, false, '0.8 g per kg is the minimum for health; lifters usually aim higher.');
    for (const [key, mgPerKg] of Object.entries(EAA_MG_PER_KG)) set(key, (mgPerKg * w) / 1000, 'RDA');
  } else {
    notes.push('Add a weigh-in to see protein and amino acid targets (they are per kg of body weight).');
  }

  const kcal = input.energyKcal ?? null;
  if (kcal !== null && kcal > 0) {
    set('saturated_g', (0.10 * kcal) / 9, 'DGA', null, false, 'Less than 10% of calories.');
    set('added_sugars_g', (0.10 * kcal) / 4, 'DGA', null, false, 'Less than 10% of calories.');
  }

  return { targets, notes };
}

export type TargetStatus = 'none' | 'low' | 'ok' | 'met' | 'over_limit' | 'over_ul';

/**
 * How a day's amount compares with its target.
 *  - goals: low (<50%), ok (50–99%), met (≥100%), over_ul (above a food-applicable UL)
 *  - limits: met (under the limit), over_limit (above it)
 */
export function targetStatus(amount: number | undefined, t: NutrientTarget | undefined): TargetStatus {
  if (!t || amount === undefined) return 'none';
  if (t.isLimit) return amount > t.amount ? 'over_limit' : 'met';
  if (t.ul !== null && !t.ulSupplementOnly && amount > t.ul) return 'over_ul';
  const pct = amount / t.amount;
  return pct >= 1 ? 'met' : pct >= 0.5 ? 'ok' : 'low';
}
